// Icon Service - Robust icon management with fallbacks
// This service ensures our system never breaks due to missing icons

import { 
  PresentationChartLineIcon,
  CogIcon,
  ExclamationTriangleIcon,
  ChartPieIcon,
  ArrowPathIcon,
  Bars3Icon,
  BriefcaseIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  UsersIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
  ComputerDesktopIcon,
  CircleStackIcon,
  KeyIcon,
  TagIcon,
  BoltIcon,
  VariableIcon,
  DocumentPlusIcon,
  LinkIcon,
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
  MapIcon,
  RocketLaunchIcon,
  BeakerIcon,
  AcademicCapIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  CubeTransparentIcon,
  FireIcon,
  GlobeAltIcon,
  HeartIcon,
  LightBulbIcon,
  MagnifyingGlassIcon,
  ServerIcon,
  SparklesIcon,
  StarIcon,
  SwatchIcon,
  TruckIcon,
  WifiIcon,
  ArchiveBoxIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  HomeIcon,
  BellIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

export interface IconService {
  getIcon: (iconName: string) => React.ComponentType<any>;
  getFallbackIcon: () => React.ComponentType<any>;
  isIconAvailable: (iconName: string) => boolean;
}

class IconServiceImpl implements IconService {
  private iconMap: Record<string, React.ComponentType<any>> = {
    // Core navigation icons
    'chart-bar': PresentationChartLineIcon,
    'cog': CogIcon,
    'exclamation-triangle': ExclamationTriangleIcon,
    'chart-pie': ChartPieIcon,
    'arrow-path': ArrowPathIcon,
    'bars-3': Bars3Icon,
    'briefcase': BriefcaseIcon,
    'shield-check': ShieldCheckIcon,
    'wrench-screwdriver': WrenchScrewdriverIcon,
    'document-duplicate': DocumentDuplicateIcon,
    'information-circle': InformationCircleIcon,
    'building-office': BuildingOfficeIcon,
    'user-group': UserGroupIcon,
    'users': UsersIcon,
    'cog-6-tooth': Cog6ToothIcon,
    'cpu-chip': CpuChipIcon,
    'cloud-arrow-up': CloudArrowUpIcon,
    'document-text': DocumentTextIcon,
    'puzzle-piece': PuzzlePieceIcon,
    'computer-desktop': ComputerDesktopIcon,
    'circle-stack': CircleStackIcon,
    'key': KeyIcon,
    'tag': TagIcon,
    'bolt': BoltIcon,
    'variable': VariableIcon,
    'document-plus': DocumentPlusIcon,
    'link': LinkIcon,
    'arrow-down-tray': ArrowDownTrayIcon,
    'document-chart-bar': DocumentChartBarIcon,
    'map': MapIcon,
    'rocket': RocketLaunchIcon,
    'beaker': BeakerIcon,
    'academic-cap': AcademicCapIcon,
    'banknotes': BanknotesIcon,
    'clipboard': ClipboardDocumentListIcon,
    'cube': CubeIcon,
    'cube-transparent': CubeTransparentIcon,
    'fire': FireIcon,
    'globe': GlobeAltIcon,
    'heart': HeartIcon,
    'light-bulb': LightBulbIcon,
    'magnifying-glass': MagnifyingGlassIcon,
    'server': ServerIcon,
    'sparkles': SparklesIcon,
    'star': StarIcon,
    'swatch': SwatchIcon,
    'truck': TruckIcon,
    'wifi': WifiIcon,
    'archive-box': ArchiveBoxIcon,
    'shopping-bag': ShoppingBagIcon,
    'shopping-cart': ShoppingCartIcon,
    'building-storefront': BuildingStorefrontIcon,
    'shop': BuildingStorefrontIcon,
    'store': BuildingStorefrontIcon,
    'check-circle': CheckCircleIcon,
    'database': CircleStackIcon,
    'home': HomeIcon,
    'bell': BellIcon,
    'book-open': BookOpenIcon,
    'chat-bubble-left-right': ChatBubbleLeftRightIcon,
    'calendar-days': CalendarDaysIcon,

    // Fallback mappings for common variations
    'database-icon': CircleStackIcon,
    'data-icon': CircleStackIcon,
    'storage-icon': CircleStackIcon,
    'marketplace-icon': BuildingStorefrontIcon,
    'resources-icon': ArchiveBoxIcon,
    'connection-icon': LinkIcon,
    'data-connection': LinkIcon,
    'data-conn': LinkIcon,
    
    // Direct mappings for PascalCase icon names (from database)
    'ShoppingBagIcon': ShoppingBagIcon,
    'ShoppingCartIcon': ShoppingCartIcon,
    'BuildingStorefrontIcon': BuildingStorefrontIcon,
    'MagnifyingGlassIcon': MagnifyingGlassIcon,
    'MapIcon': MapIcon,
    'BookOpenIcon': BookOpenIcon,
    'ChatBubbleLeftRightIcon': ChatBubbleLeftRightIcon,
  };

  getIcon(iconName: string): React.ComponentType<any> {
    if (!iconName) {
      return this.getFallbackIcon();
    }
    
    // Normalize icon name: convert PascalCase to kebab-case
    // e.g., "ShoppingBagIcon" -> "shopping-bag-icon" -> "shopping-bag"
    let normalizedName = iconName.toLowerCase();
    
    // If it's in PascalCase format (e.g., "ShoppingBagIcon"), convert to kebab-case
    if (/^[A-Z]/.test(iconName)) {
      // Remove "Icon" suffix if present
      normalizedName = iconName.replace(/Icon$/, '');
      // Convert PascalCase to kebab-case
      normalizedName = normalizedName.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
    }
    
    // Check if normalized icon exists
    if (this.isIconAvailable(normalizedName)) {
      return this.iconMap[normalizedName];
    }
    
    // Check original name in case it's already in the correct format
    if (this.isIconAvailable(iconName)) {
      return this.iconMap[iconName];
    }
    
    // Log missing icon for debugging
    console.warn(`IconService: Icon '${iconName}' (normalized: '${normalizedName}') not found, using fallback`);
    
    // Return fallback icon
    return this.getFallbackIcon();
  }

  getFallbackIcon(): React.ComponentType<any> {
    return PresentationChartLineIcon;
  }

  isIconAvailable(iconName: string): boolean {
    return iconName in this.iconMap;
  }

  // Add new icon mapping dynamically (useful for runtime additions)
  addIconMapping(iconName: string, iconComponent: React.ComponentType<any>): void {
    this.iconMap[iconName] = iconComponent;
  }

  // Get all available icon names (useful for debugging)
  getAvailableIcons(): string[] {
    return Object.keys(this.iconMap);
  }
}

// Export singleton instance
export const iconService = new IconServiceImpl();
